import { type Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'
import type { WatchlistEntry } from '@/types/generated/public_api'

export const watchlistEntryKey = (exchange: Exchange, symbol: string) => `${exchange}:${symbol}`

export const instrumentKey = (instrument: Pick<Instrument, 'exchange' | 'symbol'>) =>
  watchlistEntryKey(instrument.exchange, instrument.symbol)

// Option-group rows are stored as a bare currency symbol (e.g. "BTC") with no
// resolved instrument until useWatchlistOptionQuotes picks an ATM contract. Symbol
// shape (hyphens, length, ...) isn't a reliable signal for this across exchanges —
// e.g. Bybit names perps without a hyphen too ("BTCPERP", "BTCUSD"). Instead, an
// unresolved entry is inferred as a bare-currency placeholder only if its symbol
// matches a real `baseCurrency` among the instruments we've already loaded (from
// any exchange) — that's a currency code by construction, never a full ticker.
export const isWatchlistOptionEntry = (
  entry: Pick<WatchlistEntry, 'symbol'>,
  instrument: Instrument | undefined,
  knownBaseCurrencies: ReadonlySet<string>
) =>
  instrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION ||
  (!instrument && knownBaseCurrencies.has(entry.symbol.toUpperCase()))

export const buildKnownBaseCurrencies = (instruments: Instrument[]): ReadonlySet<string> =>
  new Set(
    instruments
      .map((instrument) => instrument.baseCurrency?.toUpperCase())
      .filter((currency): currency is string => Boolean(currency))
  )

export const areSameWatchlistEntries = (a: WatchlistEntry[], b: WatchlistEntry[]) =>
  a.length === b.length &&
  a.every(
    (entry, index) => entry.exchange === b[index]?.exchange && entry.symbol === b[index]?.symbol
  )

export const watchlistEntrySet = (entries: WatchlistEntry[]) =>
  new Set(entries.map((entry) => watchlistEntryKey(entry.exchange, entry.symbol)))
